import { useState } from "react";
import { Plus, Minus } from "@phosphor-icons/react";

const INTAKE_URL = "/intake";

const coachingFaqs = [
  {
    q: "Do I need a gym to do this?",
    a: "Nope. Every program is built around your equipment access. Whether you've got a full commercial gym, a Peloton + a pair of dumbbells, or just bodyweight in a hotel room — we'll make it work. Tell me what you've got in your intake and I program around it.",
  },
  {
    q: "I travel constantly. Can I still stay consistent?",
    a: "That's literally why this exists. I work full-time in corporate sales and travel a lot myself — so travel programming is baked into how I coach. You can also add the Travel Week Programming add-on for fully custom workouts the weeks you're on the road.",
  },
  {
    q: "How is this different from a generic fitness app?",
    a: "An app gives you a workout. Sweat With Slay gives you a coach. Programming is custom, accountability is real, and I'm in the app messaging with you. The Everfit platform is just the delivery mechanism — the coaching is 1:1.",
  },
  {
    q: "I'm a complete beginner. Is this for me?",
    a: "Absolutely. Essentials and Coaching are both perfect for beginners. I'll start you with foundational movement patterns, give you video demos for everything, and progress you based on how your body adapts — not some random template.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. You can pause, cancel, or switch tiers between billing cycles — just let me know before your next renewal. I'd rather have clients who stay because they want to, not because they're locked in.",
  },
  {
    q: "Do you do nutrition coaching?",
    a: "Yes — I'm a Certified Nutrition Coach. Coaching tier includes basic nutrition guidance and habit coaching; VIP includes advanced. For a fully built-out macro and habit blueprint, you can also add the Custom Macro Build add-on.",
  },
  {
    q: "How long until I see results?",
    a: "Most clients feel a difference in 2-3 weeks (energy, sleep, mood, gym confidence). Visible body composition changes typically show in 8-12 weeks with consistent training and nutrition. Performance metrics (strength, conditioning) usually move first.",
  },
  {
    q: "Who is this coaching for?",
    a: "Anyone serious about putting in the work — beginners, intermediates, returning lifters, athletes, busy professionals, weekend warriors. The brand voice leans bold and direct, but the coaching is gender-neutral. Honestly, many of my first clients were men — busy professionals, weekend athletes, and guys getting back into the gym after a long break. If you're committed and aligned with the vibe, you're in.",
  },
  {
    q: "What's your coaching background?",
    a: "I'm NASM CPT certified, NFPT certified, a Certified Nutrition Coach, and I have a Bachelor's in Business Administration. I coached at a big-box commercial gym, was a Division I lacrosse athlete, and I still work full-time in corporate sales — so I understand the busy-human grind.",
  },
  {
    q: "Can I switch tiers later?",
    a: "Yes, easily. Most clients start with Coaching, then either step up to VIP when they want more hands-on support, or drop to Essentials once they've built rock-solid habits. We adjust as your life changes.",
  },
];

const aboutFaqs = [
  {
    q: "Where did \"Slay\" come from?",
    a: "It started in tech sales — I was building relationships, slinging deals, running around like I had 48 hours in a day, and somewhere along the way \"Slay\" stuck. Then SlayJ became part of the whole thing too. To me, slay isn't about being perfect. It's about showing up, figuring it out, doing the hard thing, and refusing to quit on yourself. That's the energy behind Sweat With Slay.",
  },
  {
    q: "Why did you start coaching?",
    a: "Fitness has always been a huge part of my life, but coaching became different once I saw what it did for people beyond the workout — getting stronger, gaining confidence, moving better, starting to believe in themselves. I'm also extremely solution-oriented; in my corporate career I solve problems and help people move things forward. Coaching lets me do that in a more personal way — figuring out what's actually getting in someone's way (schedule, confidence, travel, stress, food, structure) and building a plan that fits their real life.",
  },
  {
    q: "What does your own training look like?",
    a: "Built around strength, sports, movement, and real life. I lift, play pickleball, golf, swim, bike, row, do yoga here and there, play in fun leagues, and love anything active outside. I also do cold plunges, contrast therapy, and recovery work because I put my body through a lot. Every year I do the Tunnel to Towers climb in honor of my Uncle Harry, which means a lot to me and keeps me connected to my New York roots. And yes — housework counts as movement.",
  },
  {
    q: "What do people usually get wrong about you?",
    a: "A lot of people assume Sweat With Slay is my full-time job. It's not — and honestly, that's part of why I built it the way I did. I work full-time in corporate sales, travel often, build software/product ideas, serve on the board of Tampa Bay Tech, am involved with the Innovators Council, work on house projects, DJ as SlayJ, play sports, attempt to have a social life, and somehow still end up being the class mom of every league I touch. When I say I understand busy people, I mean it. My coaching isn't built for perfect schedules. It's built for real life.",
  },
  {
    q: "What's your approach to food?",
    a: "Everyone who knows me knows I love good food. I grew up in the restaurant industry, so food has always been a big part of my life. I love grilling, I love a good meal, and I also love smart meal prep that makes a chaotic week easier. I'm not here to make food miserable or turn your life into a spreadsheet. I believe in structure, but I also believe in living. Good food and fitness can absolutely coexist — that's kind of the point.",
  },
  {
    q: "What do you do outside of fitness?",
    a: "A lot. Probably too much. I'm an ex-New Yorker living in Tampa — I love travel, tattoos, DJing as SlayJ, concerts, beach days, pool days, sports, golf, pickleball, biking, swimming, boating, house projects, building product/software ideas, tech events, and anything that feels like an adventure. I'm on the board of Tampa Bay Tech, involved with the Innovators Council, work in corporate sales, and somehow also ended up in HOA life. Basically, I like building things — businesses, relationships, playlists, routines, houses, communities, and stronger humans.",
  },
  {
    q: "Who is Sweat With Slay NOT for?",
    a: "Not for someone looking for a crash diet, quick fix, or \"six-pack in 30 days\" plan. Also not for someone who wants a coach to do the work for them. I can give you structure, accountability, programming, support, honest feedback, and a lot of hype — but you still have to show up. This is for people who are ready to stop starting over every Monday, build consistency, get stronger, and feel better in a way that actually fits their life.",
  },
  {
    q: "Random fun fact?",
    a: "I almost went down the path of becoming an amateur boxer. I met Don Elbaum on the floor of Grand Central — legendary boxing promoter, one of those people you meet and immediately think, \"Wait… is my life about to take a hard left?\" For a second I really thought I might end up boxing. Life took me in a few different directions — corporate sales, fitness, DJing, tech, business — but the competitiveness, grit, discipline, and \"let's see what I'm capable of\" mindset never really left. That energy is a huge part of Sweat With Slay.",
  },
];

function FAQItem({ q, a, open, onClick, idx }) {
  return (
    <div className="border-b border-white/10">
      <button
        onClick={onClick}
        data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-center justify-between gap-6 py-6 text-left group"
      >
        <span className="font-body font-semibold text-base md:text-lg text-white group-hover:text-[#FF1B91] transition-colors leading-snug">
          {q}
        </span>
        <span className={`flex-shrink-0 w-9 h-9 border border-white/20 flex items-center justify-center transition-all ${
          open ? "bg-[#FF1B91] border-[#FF1B91] text-white rotate-180" : "text-white group-hover:border-[#FF1B91] group-hover:text-[#FF1B91]"
        }`}>
          {open ? <Minus size={16} weight="bold" /> : <Plus size={16} weight="bold" />}
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${
          open ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-white/75 leading-relaxed pr-12 text-[15px] whitespace-pre-line">{a}</p>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ kicker, title }) {
  return (
    <div className="pt-10 pb-2" data-testid={`faq-section-${kicker.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="h-px w-8 bg-[#FF1B91]" />
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">{kicker}</p>
      </div>
      <h3 className="font-display font-black uppercase text-2xl md:text-3xl tracking-tight">
        {title}
      </h3>
    </div>
  );
}

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative px-6 md:px-12 lg:px-20 py-16 lg:py-20 border-b border-white/10 bg-black"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 max-w-7xl mx-auto">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#FF1B91]" />
              <p className="text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">FAQ / 07</p>
            </div>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.88] tracking-tighter mb-6">
              Got{" "}
              <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">questions?</span>
            </h2>
            <p className="text-white/70 leading-relaxed max-w-md mb-8">
              The most common things people ask before signing up — plus the personal stuff. Not seeing yours? DM me on Instagram or drop it in the intake.
            </p>
            <a
              href={INTAKE_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="faq-intake-cta"
              className="inline-flex items-center gap-3 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] btn-brutal btn-brutal-pink"
            >
              Start your intake →
            </a>
          </div>
        </div>

        <div className="lg:col-span-8">
          <SectionLabel kicker="The coaching / 01" title="How it works" />
          {coachingFaqs.map((f, i) => (
            <FAQItem
              key={f.q}
              q={f.q}
              a={f.a}
              idx={i}
              open={openIdx === i}
              onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}

          <SectionLabel kicker="About me / 02" title="The person behind Slay" />
          {aboutFaqs.map((f, i) => {
            const globalIdx = i + coachingFaqs.length;
            return (
              <FAQItem
                key={f.q}
                q={f.q}
                a={f.a}
                idx={globalIdx}
                open={openIdx === globalIdx}
                onClick={() => setOpenIdx(openIdx === globalIdx ? -1 : globalIdx)}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
